import { useRef } from 'react'

import * as api from 'api/api'
import { Attachment, Section } from 'api/types'
import { Icon } from 'components/Icon'
import { Button, IconButton, Modal } from 'components/ui'
import { formatDateTime, formatSize, isImage } from 'lib/format'
import { t } from 'lib/i18n'
import { useAction, useObjectUrl } from 'lib/hooks'

export const ACCEPT = 'image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt'

export interface AttachmentActions {
  upload: (section: Section, files: File[], lineId?: string) => Promise<void>
  remove: (attachment: Attachment) => Promise<void>
  canRemove: (attachment: Attachment) => boolean
}

export const Thumb = ({ attachment }: { attachment: Attachment }) => {
  const url = useObjectUrl(isImage(attachment.mimeType) ? `/attachments/${attachment.id}?inline=1` : null)
  if (!isImage(attachment.mimeType)) return <Icon name="file" size={22} />
  return url ? <img src={url} alt={attachment.caption || attachment.originalName} /> : <span className="spinner" aria-hidden="true" />
}

export const PreviewModal = ({ attachment, onClose }: { attachment: Attachment; onClose: () => void }) => {
  const url = useObjectUrl(`/attachments/${attachment.id}?inline=1`)
  const { busy, run } = useAction()
  return (
    <Modal
      title={attachment.caption || attachment.originalName}
      onClose={onClose}
      wide
      footer={
        <Button icon="download" busy={busy === 'dl'} onClick={() => run('dl', () => api.downloadAttachment(attachment.id))}>
          {t('تنزيل الأصل', 'Download original')}
        </Button>
      }
    >
      <div className="preview">
        {url ? <img src={url} alt={attachment.caption || attachment.originalName} /> : <span className="spinner" aria-hidden="true" />}
      </div>
      <p className="muted">
        {attachment.originalName} · {formatSize(attachment.size)} · {attachment.uploadedByName} · {formatDateTime(attachment.createdAt)}
      </p>
    </Modal>
  )
}

export const UploadButton = ({
  label,
  onFiles,
  busy,
  variant = 'secondary',
  required,
}: {
  label?: string
  onFiles: (files: File[]) => void
  busy?: boolean
  variant?: 'secondary' | 'ghost' | 'primary'
  required?: boolean
}) => {
  const input = useRef<HTMLInputElement>(null)
  return (
    <>
      <Button
        size="sm"
        variant={variant}
        icon="paperclip"
        busy={busy}
        className={required ? 'btn--required' : undefined}
        onClick={() => input.current?.click()}
      >
        {label ?? t('إرفاق', 'Attach')}
      </Button>
      <input
        ref={input}
        type="file"
        multiple
        accept={ACCEPT}
        hidden
        onChange={(event) => {
          const files = Array.from(event.target.files ?? [])
          event.target.value = ''
          if (files.length) onFiles(files)
        }}
      />
    </>
  )
}

/** Compact chips for files attached to one line or one section. */
export const AttachmentChips = ({
  attachments,
  actions,
  onPreview,
}: {
  attachments: Attachment[]
  actions: AttachmentActions
  onPreview: (attachment: Attachment) => void
}) => {
  const { busy, run } = useAction()
  if (!attachments.length) return null
  return (
    <ul className="chips">
      {attachments.map((attachment) => (
        <li key={attachment.id} className="file-chip">
          <button
            type="button"
            className="file-chip__open"
            title={`${attachment.originalName} (${formatSize(attachment.size)})`}
            onClick={() =>
              isImage(attachment.mimeType)
                ? onPreview(attachment)
                : run(attachment.id, () => api.downloadAttachment(attachment.id))
            }
          >
            <Icon name={isImage(attachment.mimeType) ? 'image' : 'file'} size={14} />
            <span>{attachment.caption || attachment.originalName}</span>
          </button>
          {actions.canRemove(attachment) ? (
            <IconButton
              icon="close"
              label={t(`حذف ${attachment.originalName}`, `Delete ${attachment.originalName}`)}
              className="file-chip__remove"
              disabled={busy === `rm${attachment.id}`}
              onClick={() => run(`rm${attachment.id}`, () => actions.remove(attachment))}
            />
          ) : null}
        </li>
      ))}
    </ul>
  )
}

/** Thumbnail gallery for section-level files (e.g. site photos under works). */
export const Gallery = ({
  attachments,
  actions,
  onPreview,
}: {
  attachments: Attachment[]
  actions: AttachmentActions
  onPreview: (attachment: Attachment) => void
}) => {
  const { busy, run } = useAction()
  if (!attachments.length) return null
  return (
    <ul className="gallery">
      {attachments.map((attachment) => (
        <li key={attachment.id}>
          <button
            type="button"
            className="gallery__item"
            onClick={() =>
              isImage(attachment.mimeType)
                ? onPreview(attachment)
                : run(attachment.id, () => api.downloadAttachment(attachment.id))
            }
          >
            <span className="gallery__thumb">
              <Thumb attachment={attachment} />
            </span>
            <span className="gallery__name">{attachment.caption || attachment.originalName}</span>
          </button>
          {actions.canRemove(attachment) ? (
            <IconButton
              icon="trash"
              label={t(`حذف ${attachment.originalName}`, `Delete ${attachment.originalName}`)}
              className="gallery__remove"
              disabled={busy === `rm${attachment.id}`}
              onClick={() => run(`rm${attachment.id}`, () => actions.remove(attachment))}
            />
          ) : null}
        </li>
      ))}
    </ul>
  )
}